import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';

const ErrorContainer = styled.div`
  padding: 2rem;
  text-align: center;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 0.25rem;
  margin: 1rem;
`;

const ErrorTitle = styled.h2`
  color: #721c24;
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  color: #721c24;
  margin-bottom: 1rem;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const RetryButton = styled.button`
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  cursor: pointer;

  &:hover {
    background-color: #c82333;
  }
`;

// jsdom's window.location is not writable, so a test cannot stub
// window.location.reload() directly. Routing the reload through a method on
// this exported object gives ErrorBoundary.test.tsx a seam: it replaces
// reloadPage.run with a jest.fn() before mounting the boundary, and the
// component below always calls it through the same object reference.
export const reloadPage = {
  run: (): void => {
    window.location.reload();
  },
};

// Once-per-tab-session guard so a persistently failing deploy (the new chunk
// is ALSO broken, or never stops changing) cannot loop automatic reloads.
const CHUNK_RELOAD_GUARD_KEY = 'tcg-calendar-chunk-reload-attempted';

const CHUNK_LOAD_ERROR_PATTERNS = [
  /Loading chunk/i,
  /Loading CSS chunk/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
];

// A stale tab that navigates to a lazy route whose hashed chunk was deleted
// by a newer deploy (a plain 404 since PR #49) gets this shape of error, not
// a normal runtime error. webpack names it ChunkLoadError; browsers loading
// native ES module chunks report their own wording instead.
export const isChunkLoadError = (error: Error): boolean => {
  if (error.name === 'ChunkLoadError') return true;
  const message = error.message || '';
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  // True once this tab has already used its one automatic chunk-error
  // reload (or storage is blocked, so the guard cannot be trusted): the
  // fallback then offers a manual "Reload page" button instead of retrying
  // the reload on its own.
  chunkReloadGuardTripped: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, chunkReloadGuardTripped: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  // An arrow class field (like the handlers below), not a plain method: a
  // plain componentDidCatch(...) {} calling this.handleChunkLoadError()
  // trips this eslint-plugin-react build's no-is-mounted rule, which (bug in
  // the installed version) flags ANY this.foo() call made from inside real
  // class-method syntax, not just isMounted.
  componentDidCatch = (error: Error, errorInfo: ErrorInfo) => {
    console.error('Error caught by boundary:', error, errorInfo);

    if (isChunkLoadError(error)) {
      this.handleChunkLoadError();
    }
  };

  // Returns true when an automatic reload has ALREADY happened this tab
  // session (or storage access failed, which is treated the same way: fail
  // safe to the manual button rather than trust a guard we cannot read back).
  private tripChunkReloadGuard(): boolean {
    try {
      if (window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === 'true') {
        return true;
      }
      window.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, 'true');
      return false;
    } catch (error) {
      return true;
    }
  }

  private handleChunkLoadError = () => {
    const alreadyAttempted = this.tripChunkReloadGuard();
    if (alreadyAttempted) {
      this.setState({ chunkReloadGuardTripped: true });
      return;
    }
    reloadPage.run();
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, chunkReloadGuardTripped: false });
  };

  handleReloadClick = () => {
    reloadPage.run();
  };

  render() {
    if (this.state.hasError) {
      if (this.state.chunkReloadGuardTripped) {
        return (
          <ErrorContainer>
            <ErrorTitle>This page needs a refresh</ErrorTitle>
            <ErrorMessage>
              We've released an update and this part of the site needs a refresh to keep working.
              Reload the page to continue.
            </ErrorMessage>
            <ButtonRow>
              <RetryButton onClick={this.handleReloadClick}>
                Reload page
              </RetryButton>
              <RetryButton onClick={this.handleRetry}>
                Try Again
              </RetryButton>
            </ButtonRow>
          </ErrorContainer>
        );
      }

      return (
        <ErrorContainer>
          <ErrorTitle>Something went wrong</ErrorTitle>
          <ErrorMessage>
            We're sorry, but something unexpected happened. Please try refreshing the page.
          </ErrorMessage>
          <RetryButton onClick={this.handleRetry}>
            Try Again
          </RetryButton>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;